import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ComplaintFormData, FormState, FormErrors, ApiResponse } from '../types';
import { validateRequired, validateEmail, validateMaxLength, validateMinDate } from '../utils';
import { VALIDATION_RULES } from '../constants';

const initialFormData: ComplaintFormData = {
  firstName: '',
  lastName: '',
  streetAddress: '',
  addressLine2: '',
  city: '',
  stateProvince: '',
  zipPostal: '',
  country: '',
  phone: '',
  email: '',
  productName: '',
  color: '',
  serialNumber: '',
  issueDescription: '',
  issueDate: '',
  retailer: '',
  purchaseDate: '',
  issuePhotos: null,
  productPhotos: null,
  receipt: null
};

const initialFormState: FormState = {
  data: initialFormData,
  errors: {},
  isSubmitting: false,
  submitStatus: '',
  submitStatusType: ''
};

export const useContactForm = () => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<FormState>(initialFormState);

  const validateForm = useCallback((data: ComplaintFormData): FormErrors => {
    const errors: FormErrors = {};
    const maxLength = VALIDATION_RULES.textField.maxLength;
    const nameMaxLength = VALIDATION_RULES.name.maxLength;

    // Required text fields
    if (!validateRequired(data.firstName)) {
      errors.firstName = t('validation.required') || 'Please enter your first name.';
    } else if (!validateMaxLength(data.firstName, nameMaxLength)) {
      errors.firstName = t('validation.maxLength', { max: nameMaxLength }) || `This field must not exceed ${nameMaxLength} characters.`;
    }

    if (!validateRequired(data.lastName)) {
      errors.lastName = t('validation.required') || 'Please enter your last name.';
    } else if (!validateMaxLength(data.lastName, nameMaxLength)) {
      errors.lastName = t('validation.maxLength', { max: nameMaxLength }) || `This field must not exceed ${nameMaxLength} characters.`;
    }

    if (!validateRequired(data.streetAddress)) {
      errors.streetAddress = t('validation.required') || 'Please enter your street address.';
    } else if (!validateMaxLength(data.streetAddress, maxLength)) {
      errors.streetAddress = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (data.addressLine2 && !validateMaxLength(data.addressLine2, maxLength)) {
      errors.addressLine2 = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (!validateRequired(data.city)) {
      errors.city = t('validation.required') || 'Please enter your city.';
    } else if (!validateMaxLength(data.city, maxLength)) {
      errors.city = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (data.stateProvince && !validateMaxLength(data.stateProvince, maxLength)) {
      errors.stateProvince = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (!validateRequired(data.zipPostal)) {
      errors.zipPostal = t('validation.required') || 'Please enter your zip/postal code.';
    } else if (!validateMaxLength(data.zipPostal, maxLength)) {
      errors.zipPostal = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (!validateRequired(data.country)) {
      errors.country = t('validation.required') || 'Please select your country.';
    }

    if (!validateRequired(data.phone)) {
      errors.phone = t('validation.required') || 'Please enter your phone number with the area code.';
    } else if (!validateMaxLength(data.phone, maxLength)) {
      errors.phone = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (!validateRequired(data.email)) {
      errors.email = t('validation.required') || 'Please enter your email.';
    } else if (!validateEmail(data.email)) {
      errors.email = t('validation.email') || 'Please enter a valid email address.';
    } else if (!validateMaxLength(data.email, maxLength)) {
      errors.email = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (!validateRequired(data.productName)) {
      errors.productName = t('validation.required') || 'Please enter your Dreamfire product name or describe the product.';
    } else if (!validateMaxLength(data.productName, maxLength)) {
      errors.productName = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (data.color && !validateMaxLength(data.color, maxLength)) {
      errors.color = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (data.serialNumber && !validateMaxLength(data.serialNumber, maxLength)) {
      errors.serialNumber = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (!validateRequired(data.issueDescription)) {
      errors.issueDescription = t('validation.required') || 'Please provide details about the product problem.';
    }

    if (!validateRequired(data.issueDate)) {
      errors.issueDate = t('validation.required') || 'Please specify the date when the problem with the product first occurred.';
    } else if (!validateMinDate(data.issueDate, VALIDATION_RULES.date.min)) {
      errors.issueDate = t('validation.minDate', { min: VALIDATION_RULES.date.min }) || `Please enter a date on or after ${VALIDATION_RULES.date.min}.`;
    }

    if (!validateRequired(data.retailer)) {
      errors.retailer = t('validation.required') || 'Please enter the name of the official dealer who you bought the product from.';
    } else if (!validateMaxLength(data.retailer, maxLength)) {
      errors.retailer = t('validation.maxLength', { max: maxLength }) || `This field must not exceed ${maxLength} characters.`;
    }

    if (!validateRequired(data.purchaseDate)) {
      errors.purchaseDate = t('validation.required') || 'Please enter the date of purchase as stated on the receipt.';
    } else if (!validateMinDate(data.purchaseDate, VALIDATION_RULES.date.min)) {
      errors.purchaseDate = t('validation.minDate', { min: VALIDATION_RULES.date.min }) || `Please enter a date on or after ${VALIDATION_RULES.date.min}.`;
    }

    // Validate file uploads
    const validateFileUpload = (files: FileList | null, fieldName: string, isRequired: boolean = true): string | undefined => {
      if (!files || files.length === 0) {
        if (isRequired) {
          return t('validation.required') || 'Please upload a file.';
        }
        return undefined;
      }

      const fileArray = Array.from(files);
      
      // Validate file types (images and PDFs)
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      const invalidFiles = fileArray.filter(file => !allowedTypes.includes(file.type));
      
      if (invalidFiles.length > 0) {
        return 'Please upload only JPG, PNG, or PDF files.';
      }

      // Validate file size (max 10MB per file)
      const maxFileSize = 10 * 1024 * 1024; // 10MB in bytes
      const oversizedFiles = fileArray.filter(file => file.size > maxFileSize);
      
      if (oversizedFiles.length > 0) {
        return 'Maximum file size: 10MB.';
      }

      return undefined;
    };

    const issuePhotosError = validateFileUpload(data.issuePhotos, 'issuePhotos', true);
    if (issuePhotosError) {
      errors.issuePhotos = issuePhotosError;
    }

    const productPhotosError = validateFileUpload(data.productPhotos, 'productPhotos', true);
    if (productPhotosError) {
      errors.productPhotos = productPhotosError;
    }

    const receiptError = validateFileUpload(data.receipt, 'receipt', false);
    if (receiptError) {
      errors.receipt = receiptError;
    }

    return errors;
  }, [t]);

  const updateField = useCallback((name: keyof ComplaintFormData, value: string | FileList | null) => {
    setFormState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [name]: value
      },
      errors: {
        ...prev.errors,
        [name]: undefined // Clear field error when user starts typing
      },
      submitStatus: '', // Clear any status message when user starts typing
      submitStatusType: ''
    }));
  }, []);

  const setSubmitting = useCallback((isSubmitting: boolean) => {
    setFormState(prev => ({
      ...prev,
      isSubmitting
    }));
  }, []);

  const setSubmitStatus = useCallback((status: string, type: 'success' | 'error' | '' = '') => {
    setFormState(prev => ({
      ...prev,
      submitStatus: status,
      submitStatusType: type
    }));
  }, []);

  const setErrors = useCallback((errors: FormErrors) => {
    setFormState(prev => ({
      ...prev,
      errors
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormState(initialFormState);
  }, []);

  const handleSubmit = useCallback(async (
    onSubmit: (data: ComplaintFormData, token: string, source?: string) => Promise<ApiResponse>,
    token: string,
    source?: string
  ) => {
    const errors = validateForm(formState.data);
    
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return false;
    }

    setSubmitting(true);
    setSubmitStatus('', '');
    // Clear any previous general errors
    setErrors({ ...formState.errors, general: undefined });

    try {
      const response = await onSubmit(formState.data, token, source);
      if (response.success) {
        // Use translated success message instead of API message
        const successMessage = t('messages.success');
        // Reset form data but keep the success message
        setFormState({
          data: initialFormData,
          errors: {},
          isSubmitting: false,
          submitStatus: successMessage,
          submitStatusType: 'success'
        });
        return true;
      } else {
        // Handle different error types with appropriate user feedback
        const errorMessage = response.message;
        const errorCode = response.error;
        
        // For certain errors, show as general form error instead of status message
        const showAsGeneralError = errorCode && [
          'API_URL_NOT_CONFIGURED',
          'FILE_PROCESSING_ERROR',
          'HTTP_400',
          'HTTP_401',
          'HTTP_403'
        ].includes(errorCode);

        if (showAsGeneralError) {
          setErrors({
            ...formState.errors,
            general: errorMessage
          });
          setSubmitStatus('', '');
        } else {
          setSubmitStatus(errorMessage, 'error');
        }
        
        // Log error for debugging
        console.error('Form submission failed:', { errorCode, errorMessage });
        
        return false;
      }
    } catch (error) {
      console.error('Unexpected error during form submission:', error);
      
      // Handle unexpected errors (shouldn't happen with improved API error handling)
      const errorMessage = 'Ootamatu viga vormi saatmisel. Palun proovige hiljem uuesti.';
      setErrors({
        ...formState.errors,
        general: errorMessage
      });
      setSubmitStatus('', '');
      
      return false;
    } finally {
      // Always ensure loading state is cleared
      setSubmitting(false);
    }
  }, [formState.data, formState.errors, validateForm, setSubmitting, setSubmitStatus, setErrors, t]);

  return {
    formState,
    updateField,
    setSubmitting,
    setSubmitStatus,
    setErrors,
    resetForm,
    handleSubmit,
    validateForm
  };
};
